import { useState, useMemo } from "react";
import { useQuery } from "react-query";
import { Container, Card, Grid, Stack, Typography, Button, Box, Pagination, Skeleton } from "@mui/material";
import LoadingButton from "@mui/lab/LoadingButton";
import axios from "axios";

import addCount from "Helper/addCount";

import { API_URL, BASE_URL } from "Constants";

import Modal from "Components/Modal";
import Score from "Components/Score";

import { toast } from "react-toastify";
import { Link } from "react-router-dom";

interface IData {
  data: IService[];
  current_page: number;
  last_page: number;
}
interface IService {
  crossing: { name: string; link: string }[];
  id: number;
  name: string;
  picture: string;
  score: number;
  special_level: boolean;
  score_status: boolean;
}
interface IPagination {
  page: number;
  last_page: number;
}

export default function Home() {
  const [page, setPage] = useState<number>(1);
  const [showModal, setShowModal] = useState<boolean>(false);
  const [selectedService, setSelectedService] = useState<IService | null>(null);
  const [score, setScore] = useState<number | null>(null);
  const [savingScore, setSavingScore] = useState<boolean>(false);
  const [searchInput, setSearchInput] = useState<string>("");
  const [query, setQuery] = useState<string>("");

  const { data, isLoading } = useQuery({
    queryKey: ["services", page, query],
    queryFn: (): Promise<IData> =>
      axios.get(`${API_URL}/services?page=${page}${query ? `&q=${query}` : ""}`).then((res) => res.data),
  });
  const services = data?.data || [];
  const pagination = data || { current_page: 1, last_page: 1 };

  // Api functions
  const saveScore = () => {
    setSavingScore(true);
    let formData = new FormData();
    formData.append("value", String(score));
    axios.post(`${API_URL}/score/create/${selectedService?.id}`, formData).then((res) => {
      toast.success("با موفقیت ثبت شد");
      setSavingScore(false);
      setShowModal(false);
    });
  };

  // Logic functions
  const handleSelectService = (service: IService) => {
    addCount("service", service.id);
    setSelectedService(service);
    toggleModal();
  };
  const handleSearchService = () => {
    setPage(1);
    setQuery(searchInput);
  };
  const toggleModal = () => setShowModal((prev) => !prev);

  // UI functions
  const renderServices = useMemo(() => {
    return services?.map((i) => (
      <Grid item xs={12} md={4} lg={3}>
        <Button sx={{ width: "100%", p: 0, borderRadius: "15px", height: "100%" }} onClick={() => handleSelectService(i)}>
          <Card
            sx={{
              width: "100%",
              height: "100%",
              p: 2,
              px: 3,
              position: "relative",
              display: "grid",
              placeItems: "center",
              bgcolor: "#fff",
            }}
          >
            <Grid container alignItems="center" columnSpacing={5}>
              <Grid item xs={4}>
                <img src={`${BASE_URL}${i.picture}`} style={{ width: "100%", maxWidth: "80px" }} />
              </Grid>
              <Grid item xs={8}>
                <Stack spacing={0.5} alignItems="flex-start">
                  <Typography sx={{ color: "#A4A4A4" }}>نام سرویس</Typography>
                  <Typography
                    variant="h5"
                    fontWeight="600"
                    noWrap
                    sx={{ width: "100%", direction: "rtl", textTransform: "none" }}
                    align="left"
                  >
                    {i.name}
                  </Typography>
                  <Typography sx={{ color: "#A4A4A4" }}>راه حل گذر از تحریم</Typography>
                  <Typography variant="h5" fontWeight="600" noWrap sx={{ width: "100%" }} align="left">
                    {i.crossing.map((i) => i.name).join(",")}
                  </Typography>
                </Stack>
              </Grid>
            </Grid>
          </Card>
        </Button>
      </Grid>
    ));
  }, [services, toggleModal]);
  const renderLoadingItems = useMemo(() => {
    return Array.from({ length: 8 }).map((i) => (
      <Grid item xs={12} md={4} lg={3}>
        <Skeleton variant="rectangular" width="100%" sx={{ borderRadius: 3, height: "155px" }} />
      </Grid>
    ));
  }, []);

  return (
    <>
      <Modal open={showModal} onClose={toggleModal} size="sm">
        <Stack alignItems="center" spacing={3}>
          <img src={`${BASE_URL}${selectedService?.picture}`} style={{ height: "100px" }} />

          <Box
            sx={{
              width: "80%",
              height: "2px",
              background: "linear-gradient(180deg, rgba(0, 0, 0, 0) 7.81%, #000000 49.48%, rgba(0, 0, 0, 0) 98.44%)",
            }}
          />

          <Stack alignItems="center" spacing={0.5}>
            <Typography color="text.secondary" variant="h6">
              نام سرویس:
            </Typography>
            <Typography variant="h4" align="center">
              {selectedService?.name}
            </Typography>
          </Stack>

          <Stack alignItems="center" spacing={0.5}>
            <Typography color="text.secondary" variant="h6">
              راه حل گذر از تحریم:
            </Typography>
            {selectedService?.crossing.map((i) => (
              <Link to={i.link}>
                <Typography variant="h4" align="center">
                  {i.name}
                </Typography>
              </Link>
            ))}
          </Stack>

          {selectedService?.score_status ? (
            <Typography variant="h6">امتیاز شما ثبت شد</Typography>
          ) : (
            <>
              <Score score={score} setScore={(value) => setScore(value)} />
              <LoadingButton loading={savingScore} variant="contained" size="large" onClick={saveScore}>
                ثبت امتیاز
              </LoadingButton>
            </>
          )}
        </Stack>
      </Modal>

      <Container maxWidth="xl">
        <Grid container mb={4}>
          <Grid item xs={12} md={6} lg={4}>
            <Card sx={{ p: 1, pl: 2, "& input": { fontSize: { xs: "15px", md: "18px" }, paddingTop: "2px" } }}>
              <Stack direction="row" spacing={1}>
                <input
                  type="text"
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") handleSearchService();
                  }}
                  placeholder="جستجوی سرویس"
                  style={{
                    background: "transparent",
                    border: "none",
                    outline: "none",
                    color: "#333",
                    fontFamily: "Kalameh",
                    width: "100%",
                  }}
                />

                <LoadingButton
                  loading={isLoading}
                  variant="contained"
                  color="secondary"
                  sx={{ width: "150px", fontSize: "18px", borderRadius: "10px" }}
                  onClick={handleSearchService}
                >
                  جستجو
                </LoadingButton>
              </Stack>
            </Card>
          </Grid>
        </Grid>

        {!isLoading && services?.length === 0 && (
          <Stack alignItems="center" spacing={5}>
            <Typography align="center">
              این سرویس در حال حاضر پشتیبانی نمیشود . برای اضافه شدن این سرویس از طریق لینک زیر درخواست خود را ارسال کنید
            </Typography>

            <Link to="/request">
              <Button
                color="success"
                variant="contained"
                size="large"
                sx={{ fontSize: "18px", height: "50px", minWidth: "200px" }}
              >
                ثبت درخواست
              </Button>
            </Link>
          </Stack>
        )}

        <Grid container columnSpacing={4} rowGap={4}>
          {isLoading ? renderLoadingItems : renderServices}
        </Grid>

        {services?.length > 0 && (
          <Stack alignItems="center" mt={5}>
            <Pagination
              count={pagination.last_page}
              page={page}
              onChange={(e, value) => setPage(value)}
              size="large"
              siblingCount={1}
              boundaryCount={1}
              sx={{ display: { xs: "none", md: "block" } }}
            />

            <Pagination
              count={pagination.last_page}
              page={page}
              onChange={(e, value) => setPage(value)}
              size="large"
              siblingCount={0}
              boundaryCount={1}
              sx={{ display: { md: "none" } }}
            />
          </Stack>
        )}
      </Container>
    </>
  );
}
